export interface SWOTExample {
  industry: string;
  company: string;
  strengths: string[];
  weaknesses: string[];
  opportunities: string[];
  threats: string[];
}

export interface Technique {
  id: string;
  name: string;
  category: "strategic" | "prioritization" | "elicitation" | "modeling";
  shortDesc: string;
  description: string;
  when: string;
  fields: TechniqueField[];
  examples?: SWOTExample[];
}

export interface TechniqueField {
  id: string;
  label: string;
  placeholder: string;
  type: "textarea" | "list";
}

export const TECHNIQUES: Technique[] = [
  {
    id: "swot",
    name: "SWOT Analysis",
    category: "strategic",
    shortDesc: "Strengths, Weaknesses, Opportunities, Threats",
    description:
      "Situation analysis examining internal strengths/weaknesses and external opportunities/threats. Use for new projects, feature decisions, or competitive analysis.",
    when: "First call, brand-new project, report preparation, process automation decisions",
    fields: [
      { id: "strengths", label: "Strengths", placeholder: "Brand recognition, existing user base, strong dev team…", type: "list" },
      { id: "weaknesses", label: "Weaknesses", placeholder: "Thin margins, legacy systems, limited testing budget…", type: "list" },
      { id: "opportunities", label: "Opportunities", placeholder: "New markets, competitor gaps, AI integration…", type: "list" },
      { id: "threats", label: "Threats", placeholder: "Compliance changes, budget cuts, competitor features…", type: "list" },
    ],
    examples: [
      {
        industry: "Streaming & Media",
        company: "Netflix",
        strengths: ["Global brand recognition", "Vast proprietary content library", "Personalisation algorithm", "Strong original productions (Emmy-winning)"],
        weaknesses: ["Very high content acquisition costs", "Password-sharing revenue loss", "No live sports offering", "Churn sensitivity to price increases"],
        opportunities: ["Expansion into emerging markets (SE Asia, Africa)", "Ad-supported subscription tier", "Live sports & events streaming", "Gaming vertical growth"],
        threats: ["Disney+, HBO Max, Apple TV+ competition", "Content piracy", "Regulatory content quotas per country", "Rising broadband costs for users"],
      },
      {
        industry: "Electric Vehicles",
        company: "Tesla",
        strengths: ["First-mover EV brand equity", "Proprietary Supercharger network", "Software-over-the-air update capability", "Vertically integrated supply chain"],
        weaknesses: ["High vehicle price point vs. competitors", "Service centre capacity gaps", "CEO brand volatility", "Production bottlenecks at scale"],
        opportunities: ["Government EV purchase incentives", "Energy storage & solar market", "Full self-driving commercialisation", "Fleet & ride-share partnerships"],
        threats: ["Legacy OEMs launching competitive EVs", "Chinese EV manufacturers (BYD)", "Lithium & cobalt raw-material cost spikes", "Autonomous-driving regulation uncertainty"],
      },
      {
        industry: "Community Banking",
        company: "Regional Credit Union",
        strengths: ["Deep community trust & relationships", "Personalised member service", "Local market & regulatory knowledge", "Lower fee structure than big banks"],
        weaknesses: ["Limited technology & digital banking budget", "Smaller product range", "Ageing member demographic", "Fewer branch locations"],
        opportunities: ["Fintech API partnerships (open banking)", "Surge in mobile-first banking demand", "SBA loan programmes for local SMEs", "Young professional membership drives"],
        threats: ["Neobanks (Chime, Revolut) with zero-fee accounts", "Big-bank branch expansion into local area", "Rising interest-rate pressure on margins", "Cybersecurity threats targeting smaller institutions"],
      },
    ],
  },
  {
    id: "most",
    name: "MOST Analysis",
    category: "strategic",
    shortDesc: "Mission, Objectives, Strategies, Tactics",
    description:
      "Aligns project work with organizational mission. Must be conducted with leadership to ensure strategic alignment across regions and brands.",
    when: "Project kickoff with leadership, expansion planning, regional rollouts",
    fields: [
      { id: "mission", label: "Mission", placeholder: "What is the organization's overarching purpose?", type: "textarea" },
      { id: "objectives", label: "Objectives", placeholder: "Specific measurable goals to achieve the mission…", type: "list" },
      { id: "strategies", label: "Strategies", placeholder: "High-level approaches to meet objectives…", type: "list" },
      { id: "tactics", label: "Tactics", placeholder: "Specific actions and tasks to execute strategies…", type: "list" },
    ],
  },
  {
    id: "pestle",
    name: "PESTLE Analysis",
    category: "strategic",
    shortDesc: "Political, Economic, Social, Technological, Legal, Environmental",
    description:
      "Examines external macro-environmental factors affecting a project. Critical for multi-state rollouts where each market has different compliance requirements.",
    when: "Launching products in new states/markets, compliance review, regulatory assessment",
    fields: [
      { id: "political", label: "Political", placeholder: "Government policies, regulations, state laws…", type: "list" },
      { id: "economic", label: "Economic", placeholder: "Market conditions, funding, profit margins…", type: "list" },
      { id: "social", label: "Social", placeholder: "Demographics, language needs, cultural factors…", type: "list" },
      { id: "technological", label: "Technological", placeholder: "Tech stack, AI adoption, system integrations…", type: "list" },
      { id: "legal", label: "Legal", placeholder: "HIPAA, FERPA, state compliance, data privacy…", type: "list" },
      { id: "environmental", label: "Environmental", placeholder: "Sustainability, infrastructure, accessibility…", type: "list" },
    ],
  },
  {
    id: "catwoe",
    name: "CATWOE Analysis",
    category: "strategic",
    shortDesc: "Customers, Actors, Transformation, Worldview, Owners, Environment",
    description:
      "Identifies problems by examining stakeholder perspectives. High-end technique from Lancaster University, particularly useful for marketing-oriented projects with difficult stakeholders.",
    when: "Complex stakeholder environments, product launches, system redesign",
    fields: [
      { id: "customers", label: "Customers", placeholder: "Who benefits from the system output?", type: "list" },
      { id: "actors", label: "Actors", placeholder: "Who performs the transformation processes?", type: "list" },
      { id: "transformation", label: "Transformation", placeholder: "What core process delivers value?", type: "textarea" },
      { id: "worldview", label: "Worldview", placeholder: "What beliefs/values justify this transformation?", type: "textarea" },
      { id: "owners", label: "Owners", placeholder: "Who can change or shut down the system?", type: "list" },
      { id: "environment", label: "Environment", placeholder: "What constraints/rules cannot be changed?", type: "list" },
    ],
  },
  {
    id: "moscow",
    name: "MoSCoW Prioritization",
    category: "prioritization",
    shortDesc: "Must, Should, Could, Won't have",
    description:
      "Technical prioritization technique for requirements and features. Determines what must ship in the next sprint vs. what can wait. Similar to Eisenhower matrix for task management.",
    when: "Sprint planning, feature prioritization, scope definition, requirement sign-off",
    fields: [
      { id: "must", label: "Must Have", placeholder: "Username, password, login — without these we don't move forward…", type: "list" },
      { id: "should", label: "Should Have", placeholder: "OCR scanning, error notifications with specific reasons…", type: "list" },
      { id: "could", label: "Could Have", placeholder: "Nice-to-have features, UI enhancements…", type: "list" },
      { id: "wont", label: "Won't Have (this time)", placeholder: "Out of scope for current sprint, future consideration…", type: "list" },
    ],
  },
  {
    id: "usecase",
    name: "Use Case Modeling",
    category: "modeling",
    shortDesc: "Actor-system interactions with triggers and actions",
    description:
      "Documents how actors interact with a system. Combines use cases with UML diagrams. Each use case has triggers, preconditions, main flow, and alternative flows.",
    when: "Requirement documentation, system design, functional specification",
    fields: [
      { id: "actors", label: "Actors", placeholder: "Member, Admin, System, External API…", type: "list" },
      { id: "preconditions", label: "Preconditions", placeholder: "User must be authenticated, valid session…", type: "list" },
      { id: "mainflow", label: "Main Flow", placeholder: "1. User clicks login\n2. System validates credentials\n3. Dashboard loads…", type: "textarea" },
      { id: "altflows", label: "Alternative Flows", placeholder: "Invalid credentials → show error\nSession expired → redirect to login…", type: "textarea" },
    ],
  },
  {
    id: "fivewhys",
    name: "The 5 Whys",
    category: "elicitation",
    shortDesc: "Root cause analysis through iterative questioning",
    description:
      "Drill down to the root cause of a problem by asking 'why' five times. Commonly used in marketing and operations to uncover underlying issues.",
    when: "Bug triage, process failures, incident review, root cause analysis",
    fields: [
      { id: "problem", label: "Problem Statement", placeholder: "What is the issue you're investigating?", type: "textarea" },
      { id: "why1", label: "Why? (1st)", placeholder: "First-level cause…", type: "textarea" },
      { id: "why2", label: "Why? (2nd)", placeholder: "Why does the first cause happen?", type: "textarea" },
      { id: "why3", label: "Why? (3rd)", placeholder: "Going deeper…", type: "textarea" },
      { id: "why4", label: "Why? (4th)", placeholder: "Almost at root cause…", type: "textarea" },
      { id: "why5", label: "Root Cause", placeholder: "The fundamental reason…", type: "textarea" },
    ],
  },
  {
    id: "sixhats",
    name: "Six Thinking Hats",
    category: "elicitation",
    shortDesc: "Explore decisions from six distinct perspectives",
    description:
      "Edward de Bono's technique for group thinking. Each 'hat' represents a different perspective to evaluate decisions comprehensively.",
    when: "Team decision-making, brainstorming sessions, evaluating proposals",
    fields: [
      { id: "white", label: "🤍 White Hat (Facts)", placeholder: "What data and information do we have?", type: "textarea" },
      { id: "red", label: "❤️ Red Hat (Feelings)", placeholder: "Gut reactions, emotions, intuitions…", type: "textarea" },
      { id: "black", label: "🖤 Black Hat (Caution)", placeholder: "Risks, difficulties, potential problems…", type: "textarea" },
      { id: "yellow", label: "💛 Yellow Hat (Benefits)", placeholder: "Value, advantages, optimistic view…", type: "textarea" },
      { id: "green", label: "💚 Green Hat (Creativity)", placeholder: "Alternatives, new ideas, possibilities…", type: "textarea" },
      { id: "blue", label: "💙 Blue Hat (Process)", placeholder: "Summary, next steps, action items…", type: "textarea" },
    ],
  },
  {
    id: "mindmap",
    name: "Mind Mapping",
    category: "elicitation",
    shortDesc: "Central theme with branching ideas and connections",
    description:
      "Start with a central theme, each participant writes their understanding in their own words, then connect the big picture. Everyone has their own perspective.",
    when: "Initial discovery, team alignment, brainstorming complex topics",
    fields: [
      { id: "central", label: "Central Theme", placeholder: "The core topic or question…", type: "textarea" },
      { id: "branch1", label: "Branch 1", placeholder: "First major area…", type: "list" },
      { id: "branch2", label: "Branch 2", placeholder: "Second major area…", type: "list" },
      { id: "branch3", label: "Branch 3", placeholder: "Third major area…", type: "list" },
      { id: "connections", label: "Connections & Insights", placeholder: "How do branches relate? Key patterns…", type: "textarea" },
    ],
  },
  {
    id: "brainstorming",
    name: "Brainstorming / Brainwriting",
    category: "elicitation",
    shortDesc: "Structured idea generation from team members",
    description:
      "Brainwriting is preferred over brainstorming for large groups (20+ people) — participants write ideas independently before sharing, preventing wandering discussions. BA chooses the framework.",
    when: "Feature ideation, solution design, team workshops (use brainwriting for 10+ participants)",
    fields: [
      { id: "topic", label: "Topic / Question", placeholder: "What problem are we solving?", type: "textarea" },
      { id: "ideas", label: "Ideas Generated", placeholder: "List all ideas without judgment…", type: "list" },
      { id: "grouped", label: "Grouped Themes", placeholder: "Cluster similar ideas into categories…", type: "list" },
      { id: "selected", label: "Selected Ideas", placeholder: "Top ideas after voting/discussion…", type: "list" },
    ],
  },
  {
    id: "bpm",
    name: "Business Process Modeling",
    category: "modeling",
    shortDesc: "Document current and future state processes",
    description:
      "Map business processes using BPMN or flowchart notation. BPMN has specific symbols different from flowcharts — start/end events, gateways, tasks, and swim lanes.",
    when: "Process documentation, automation planning, gap analysis, current vs future state",
    fields: [
      { id: "currentstate", label: "Current State (As-Is)", placeholder: "Document existing process steps…", type: "textarea" },
      { id: "painpoints", label: "Pain Points", placeholder: "What's broken or inefficient?", type: "list" },
      { id: "futurestate", label: "Future State (To-Be)", placeholder: "Desired improved process…", type: "textarea" },
      { id: "gaps", label: "Gap Analysis", placeholder: "Differences between current and future state…", type: "list" },
    ],
  },
  {
    id: "reqanalysis",
    name: "Requirement Analysis",
    category: "elicitation",
    shortDesc: "Elicit, analyze, model, review requirements",
    description:
      "The full lifecycle: eliciting requirements from stakeholders, analyzing feasibility, modeling with diagrams, reviewing with scrum team, and budget review (level of effort).",
    when: "Throughout the project lifecycle, sprint planning, BRD/FRD creation",
    fields: [
      { id: "elicited", label: "Elicited Requirements", placeholder: "Raw requirements gathered from stakeholders…", type: "list" },
      { id: "functional", label: "Functional Requirements", placeholder: "What the system must do…", type: "list" },
      { id: "nonfunctional", label: "Non-Functional Requirements", placeholder: "Performance, security, scalability…", type: "list" },
      { id: "assumptions", label: "Assumptions & Constraints", placeholder: "What we're assuming, budget limits, timeline…", type: "list" },
    ],
  },
];

export function getTechnique(id: string): Technique | undefined {
  return TECHNIQUES.find((t) => t.id === id);
}

export const TECHNIQUE_CATEGORIES = [
  { id: "strategic", label: "Strategic Analysis", icon: "🎯" },
  { id: "prioritization", label: "Prioritization", icon: "📊" },
  { id: "elicitation", label: "Elicitation & Discovery", icon: "🔍" },
  { id: "modeling", label: "Modeling & Documentation", icon: "📐" },
] as const;
